import React, { Fragment, useEffect, useState } from "react";
import { differenceInMonths } from "date-fns";
import { shortMonthNameArray } from "../../../utils";

const AgencySnapshotReportTableDash = ({ data = [], dateFilterColumn }) => {
  const [reportData, setReportData] = useState([]);
  const columnTitleHelper = `Based on ${
    dateFilterColumn === "created_at" ? "date of enquiry" : "event start date"
  }`;

  useEffect(() => {
    if (data && data[0]) {
      setReportData(data[0]);
    }
  }, [data]);

  const dateDifference = (firstDate, secondDate) => {
    return differenceInMonths(secondDate, firstDate);
  };

  const dateRangeHeader = (key, dateRange) => {
    let firstDate = new Date(dateRange[0]);
    let secondDate = new Date(dateRange[1]);
    if (key === "currentMonth") {
      return (
        <>
          <span>
            Current Month
            <br />
            <strong>
              {firstDate.toLocaleString("en-GB", { month: "long" })}
            </strong>
          </span>
        </>
      );
    }
    if (secondDate > new Date()) {
      return (
        <>
          <span>
            This Financial Year
            <br />
            <strong>
              {shortMonthNameArray[firstDate.getMonth()] +
                " " +
                firstDate.getFullYear() +
                " To " +
                shortMonthNameArray[secondDate.getMonth()] +
                " " +
                secondDate.getFullYear()}
            </strong>
          </span>
        </>
      );
    }
    if (secondDate < new Date()) {
      return (
        <>
          <span>
            Last Financial Year
            <br />
            <strong>
              {shortMonthNameArray[firstDate.getMonth()] +
                " " +
                firstDate.getFullYear() +
                " To " +
                shortMonthNameArray[secondDate.getMonth()] +
                " " +
                secondDate.getFullYear()}
            </strong>
          </span>
        </>
      );
    }
    let difference = dateDifference(firstDate, secondDate);
    if (difference > 1 && difference < 12) {
      return (
        shortMonthNameArray[firstDate.getMonth()] +
        " " +
        firstDate.getFullYear() +
        " To " +
        shortMonthNameArray[secondDate.getMonth()] +
        " " +
        secondDate.getFullYear()
      );
    } else if (firstDate && secondDate) {
      return `${
        shortMonthNameArray[firstDate.getMonth()]
      } ${firstDate.getUTCFullYear()} To ${
        shortMonthNameArray[secondDate.getMonth()]
      } ${secondDate.getUTCFullYear()}`;
    }
    return "";
  };

  const formatValue = value => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Fragment>
      <table className={"edge-table-no-stripe"}>
        <thead>
          <tr>
            <td>&nbsp;</td>
            {reportData &&
              reportData.length > 0 &&
              reportData.map((report, index) => {
                if (report.date_range) {
                  return (
                    <td key={index} style={{ textAlign: "right" }}>
                      <span className={"text-sm"}>
                        {dateRangeHeader(report.key, report.date_range)}
                      </span>
                    </td>
                  );
                }
              })}
          </tr>
        </thead>
        <tbody>
          {reportData && reportData.length > 0 && (
            <>
              <tr className={"bg-off-white"}>
                <td colSpan={4}>
                  <strong>Enquiries</strong>
                </td>
              </tr>
              <tr>
                <td title={columnTitleHelper}>
                  <span className={"my-1 inline-block"}>No. of Enquiries</span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[0].number_of_enquiries}</strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[1].number_of_enquiries}</strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[2].number_of_enquiries}</strong>
                </td>
              </tr>

              <tr>
                <td title={columnTitleHelper}>
                  <span className={"my-1 inline-block"}>
                    Value of Enquiries
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[0].value_of_enquiries)}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[1].value_of_enquiries)}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[2].value_of_enquiries)}
                  </strong>
                </td>
              </tr>

              <tr>
                <td title={columnTitleHelper}>
                  <span className={"my-1 inline-block"}>
                    Value of Approx Commission
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[0].commission_value_of_enquiries)}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[1].commission_value_of_enquiries)}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[2].commission_value_of_enquiries)}
                  </strong>
                </td>
              </tr>

              <tr className={"bg-off-white"}>
                <td colSpan={4}>
                  <strong>Confirmed</strong>
                </td>
              </tr>
              <tr>
                <td title={columnTitleHelper}>
                  <span className={"my-1 inline-block"}>No. of Enquiries</span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[0].number_of_confirmed_bookings}</strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[1].number_of_confirmed_bookings}</strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[2].number_of_confirmed_bookings}</strong>
                </td>
              </tr>

              <tr>
                <td title={columnTitleHelper}>
                  <span className={"my-1 inline-block"}>
                    Value of Enquiries
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[0].value_of_confirmed_bookings)}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[1].value_of_confirmed_bookings)}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[2].value_of_confirmed_bookings)}
                  </strong>
                </td>
              </tr>

              <tr>
                <td title={columnTitleHelper}>
                  <span className={"my-1 inline-block"}>
                    Value of Approx Commission
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(
                      reportData[0].commission_value_of_confirmed_bookings
                    )}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(
                      reportData[1].commission_value_of_confirmed_bookings
                    )}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(
                      reportData[2].commission_value_of_confirmed_bookings
                    )}
                  </strong>
                </td>
              </tr>

              <tr className={"bg-off-white"}>
                <td colSpan={4}>
                  <strong>Provisional</strong>
                </td>
              </tr>
              <tr>
                <td
                  title={
                    columnTitleHelper + " (includes revising confirmation)"
                  }
                >
                  <span className={"my-1 inline-block"}>No. of Enquiries</span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {reportData[0].number_of_provisional_bookings}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {reportData[1].number_of_provisional_bookings}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {reportData[2].number_of_provisional_bookings}
                  </strong>
                </td>
              </tr>

              <tr>
                <td
                  title={
                    columnTitleHelper + " (includes revising confirmation)"
                  }
                >
                  <span className={"my-1 inline-block"}>
                    Value of Enquiries
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[0].value_of_provisional_bookings)}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[1].value_of_provisional_bookings)}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[2].value_of_provisional_bookings)}
                  </strong>
                </td>
              </tr>

              <tr>
                <td
                  title={
                    columnTitleHelper + " (includes revising confirmation)"
                  }
                >
                  <span className={"my-1 inline-block"}>
                    Value of Approx Commission
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(
                      reportData[0].commission_value_of_provisional_bookings
                    )}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(
                      reportData[1].commission_value_of_provisional_bookings
                    )}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(
                      reportData[2].commission_value_of_provisional_bookings
                    )}
                  </strong>
                </td>
              </tr>

              <tr className={"bg-off-white"}>
                <td colSpan={4}>
                  <strong>Denied</strong>
                </td>
              </tr>

              <tr>
                <td title={columnTitleHelper}>
                  <span className={"my-1 inline-block"}>No. of Enquiries</span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[0].number_of_denied_bookings}</strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[1].number_of_denied_bookings}</strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[2].number_of_denied_bookings}</strong>
                </td>
              </tr>

              <tr>
                <td title={columnTitleHelper}>
                  <span className={"my-1 inline-block"}>
                    Value of Enquiries
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[0].value_of_denied_bookings)}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[1].value_of_denied_bookings)}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(reportData[2].value_of_denied_bookings)}
                  </strong>
                </td>
              </tr>

              <tr>
                <td title={columnTitleHelper}>
                  <span className={"my-1 inline-block"}>
                    Value of Approx Commission
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(
                      reportData[0].commission_value_of_denied_bookings
                    )}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(
                      reportData[1].commission_value_of_denied_bookings
                    )}
                  </strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>
                    {formatValue(
                      reportData[2].commission_value_of_denied_bookings
                    )}
                  </strong>
                </td>
              </tr>

              <tr className={"bg-off-white"}>
                <td colSpan={4}>
                  <strong>Summary</strong>
                </td>
              </tr>
              <tr>
                <td title={columnTitleHelper}>
                  <span className={"my-1 inline-block"}>Cost Savings</span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[0].total_percentage_savings}%</strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[1].total_percentage_savings}%</strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[2].total_percentage_savings}%</strong>
                </td>
              </tr>
              <tr>
                <td title={columnTitleHelper}>
                  <span className={"my-1 inline-block"}>Conversion Rate</span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[0].conversion_rate}%</strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[1].conversion_rate}%</strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[2].conversion_rate}%</strong>
                </td>
              </tr>
              <tr>
                <td title={columnTitleHelper}>
                  <span className={"my-1 inline-block"}>Commission earned</span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[0].commission_earned}%</strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[1].commission_earned}%</strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  <strong>{reportData[2].commission_earned}%</strong>
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </Fragment>
  );
};

export default AgencySnapshotReportTableDash;
